/*
  <kg-video>: eigene Bedienung fuer ein Video in der Produktgalerie. Abspielen, Ton und
  Fortschritt, sonst nichts. Kein Vollbild, kein Bild-im-Bild: Das Video bleibt in
  seiner Flaeche und sieht in jedem Browser gleich aus.
*/

/* Wer Bewegung abbestellt hat, bekommt kein Video, das von selbst losgeht. */
const WENIGER_BEWEGUNG = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Dieselbe Grenze wie in product-gallery.js: darunter vergroessert ein Tipp die Flaeche. */
const SCHMAL = window.matchMedia('(max-width: 1023px)');

class KgVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.knopf = [...this.querySelectorAll('[data-video-toggle]')];
    this.ton = this.querySelector('[data-video-mute]');
    this.leiste = this.querySelector('[data-video-seek]');

    /* Auf dem Handy nie von selbst ins Vollbild, und keine fremden Knoepfe einblenden */
    this.video.playsInline = true;
    this.video.controls = false;
    this.video.setAttribute('disablepictureinpicture', '');
    this.video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');

    this.knopf.forEach((k) => k.addEventListener('click', () => this.umschalten()));
    this.ton?.addEventListener('click', () => this.tonUmschalten());
    this.video.addEventListener('click', () => {
      /*
        Am Rechner haelt ein Klick auf das Video an und faehrt wieder an. Auf dem Handy
        gehoert der Tipp der Galerie, die damit die Flaeche vergroessert — wie bei einem
        Bild. Angehalten wird dort unten in der Leiste. Liegt das Video still, deckt die
        Flaeche des Startknopfs es ab und faengt den Tipp ab: Er faehrt wieder an, und
        erst der naechste vergroessert.
      */
      if (SCHMAL.matches) return;
      this.umschalten();
    });
    this.video.addEventListener('play', () => this.zustand());
    this.video.addEventListener('pause', () => this.zustand());
    this.video.addEventListener('ended', () => this.zustand());
    this.video.addEventListener('timeupdate', () => this.fortschritt());

    /* Tastatur und Hilfsmittel bedienen das Feld weiterhin auf ihrem Weg. */
    this.leiste?.addEventListener('input', () => {
      if (!this.video.duration) return;
      this.video.currentTime = (Number(this.leiste.value) / 1000) * this.video.duration;
    });

    this.leiste?.addEventListener('pointerdown', (ereignis) => this.greifen(ereignis));

    this.zustand();
  }

  /*
    Ziehen am Fortschritt, egal wo man anpackt.

    Das Feld von sich aus zieht nur mit, wenn man genau den Punkt erwischt — auf dem Handy
    ist das kaum zu treffen. Hier wird deshalb ab dem ersten Antippen gesprungen und von
    da an gezogen. `setPointerCapture` haelt die Bewegung beim Balken, auch wenn der Finger
    daneben rutscht.
  */
  greifen(ereignis) {
    if (!this.video.duration) return;

    this.zieht = true;
    try {
      this.leiste.setPointerCapture(ereignis.pointerId);
    } catch {
      /* Ohne Zeiger-Bindung klappt es auch, nur weniger zuverlaessig am Rand. */
    }
    this.springen(ereignis);

    const bewegen = (e) => this.springen(e);
    const loslassen = () => {
      this.zieht = false;
      this.leiste.removeEventListener('pointermove', bewegen);
      this.leiste.removeEventListener('pointerup', loslassen);
      this.leiste.removeEventListener('pointercancel', loslassen);
    };

    this.leiste.addEventListener('pointermove', bewegen);
    this.leiste.addEventListener('pointerup', loslassen);
    this.leiste.addEventListener('pointercancel', loslassen);
  }

  springen(ereignis) {
    const kasten = this.leiste.getBoundingClientRect();
    if (!kasten.width) return;
    const anteil = Math.min(1, Math.max(0, (ereignis.clientX - kasten.left) / kasten.width));
    this.leiste.value = String(Math.round(anteil * 1000));
    this.video.currentTime = anteil * this.video.duration;
  }

  umschalten() {
    if (this.video.paused) {
      this.vomBesucherAngehalten = false;
      this.video.play();
    } else {
      /* Selbst angehalten heisst angehalten — auch wenn die Galerie spaeter wieder hierher kommt. */
      this.vomBesucherAngehalten = true;
      this.video.pause();
    }
  }

  tonUmschalten() {
    this.video.muted = !this.video.muted;
    /* Hat der Besucher den Ton geholt, bleibt er beim naechsten Anlauf an. */
    this.tonGewollt = !this.video.muted;
    this.zustand();
  }

  /*
    Laeuft von selbst an, sobald die Galerie dieses Medium zeigt — stumm. Browser lassen
    Autoplay nur ohne Ton zu; den Ton holt sich der Besucher ueber den Knopf. Auf dem Handy gilt dasselbe, sobald er hergewischt hat.
  */
  async starteStumm() {
    if (!this.video || this.vomBesucherAngehalten || WENIGER_BEWEGUNG.matches) return;
    if (!this.video.paused) return;
    if (!this.tonGewollt) this.video.muted = true;
    try {
      await this.video.play();
    } catch {
      /*
        Abgelehnt — in aller Regel, weil der Ton anstand. Dann stumm, das laesst jeder Browser zu.
        Scheitert auch das, bleibt der Startknopf, und mehr ist hier nicht zu tun.
      */
      this.video.muted = true;
      this.zustand();
      try {
        await this.video.play();
      } catch {
        /* Kein Autoplay, kein Drama. */
      }
    }
  }

  /* Weitergewischt: Das Video haelt an und behaelt seine Stelle. */
  anhalten() {
    if (!this.video.paused) this.video.pause();
  }

  fortschritt() {
    /* Waehrend des Ziehens fuehrt der Finger, nicht das Video. */
    if (!this.leiste || !this.video.duration || this.zieht) return;
    this.leiste.value = String(Math.round((this.video.currentTime / this.video.duration) * 1000));
  }

  /* Nur zwei Marken setzen, welches Zeichen sichtbar ist entscheidet das CSS */
  zustand() {
    this.toggleAttribute('data-playing', !this.video.paused && !this.video.ended);
    this.toggleAttribute('data-muted', this.video.muted);
  }
}

customElements.define('kg-video', KgVideo);
